import { useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { StageBadge, ExitBadge, AlignmentBadge } from '../components/Badges';
import { Button } from '../components/ui/button';
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer,
  BarChart, Bar, CartesianGrid, Area, AreaChart, ReferenceLine,
} from 'recharts';
import { ArrowLeft, Star, Plus, AlertTriangle, ShieldCheck } from 'lucide-react';

export default function StockDetail() {
  const { symbol } = useParams();
  const navigate = useNavigate();
  const { signals, watchlist, toggleWatchlist, addRealTrade } = useApp();

  const stock = useMemo(() => signals.find(s => s.symbol === symbol), [signals, symbol]);

  if (!stock) {
    return (
      <div className="p-6" data-testid="stock-detail-empty">
        <Button variant="ghost" onClick={() => navigate('/signals')} className="text-trading-muted mb-4">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to Signals
        </Button>
        <div className="bg-trading-surface border border-trading-border rounded-sm p-16 text-center">
          <p className="text-trading-muted">Stock {symbol} not found in current signals.</p>
        </div>
      </div>
    );
  }

  const daily = stock.dailyData || [];
  const chartData = daily.slice(-30).map(d => ({
    date: d.date?.slice(5) || '',
    close: d.close,
    instNet: d.instNet,
    retailNet: d.retailNet,
    volume: d.volume,
    accumulation: d.accumulation5d,
  }));

  const handleAddTrade = () => {
    if (!stock.entryAllowed) return;
    const stopPrice = stock.price * 0.95;
    addRealTrade({
      symbol: stock.symbol,
      entryPrice: stock.price,
      stopPrice,
    });
  };

  return (
    <div className="p-6 max-w-[1600px] mx-auto" data-testid="stock-detail-module">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/signals')} className="text-trading-muted hover:text-trading-text" data-testid="back-to-signals">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <h1 className="font-heading text-2xl font-semibold tracking-tight flex items-center gap-3">
              {stock.symbol}
              <span className="font-mono text-lg text-trading-blue">PHP {stock.price?.toFixed(2)}</span>
            </h1>
            <div className="flex items-center gap-2 mt-1">
              <StageBadge stage={stock.myStage} />
              <StageBadge stage={stock.optStage} />
              <AlignmentBadge alignment={stock.alignment} />
              <ExitBadge exitMode={stock.exitMode} />
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            variant="ghost"
            onClick={() => toggleWatchlist(stock.symbol)}
            data-testid="toggle-watchlist"
            className="border border-trading-border text-trading-muted hover:text-trading-text rounded-sm"
          >
            <Star className={`w-4 h-4 mr-2 ${watchlist.includes(stock.symbol) ? 'fill-trading-amber text-trading-amber' : ''}`} />
            {watchlist.includes(stock.symbol) ? 'Watching' : 'Watch'}
          </Button>
          <Button
            onClick={handleAddTrade}
            disabled={!stock.entryAllowed}
            data-testid="add-real-trade-btn"
            className="bg-trading-blue hover:bg-trading-blue/90 text-white rounded-sm"
          >
            <Plus className="w-4 h-4 mr-2" /> Add Trade
          </Button>
        </div>
      </div>

      {/* Entry Status */}
      <div className={`mb-6 p-3 rounded-sm border flex items-center gap-2 ${
        stock.entryAllowed
          ? 'bg-trading-green/5 border-trading-green/20 text-trading-green'
          : 'bg-trading-red/5 border-trading-red/20 text-trading-red'
      }`}>
        {stock.entryAllowed
          ? <><ShieldCheck className="w-4 h-4" /> Entry allowed</>
          : <><AlertTriangle className="w-4 h-4" /> Entry blocked — distribution confirmed</>
        }
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        {/* Price Chart */}
        <ChartCard title="Price" testId="price-chart">
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={chartData}>
              <defs>
                <linearGradient id="priceGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#3B82F6" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="#242C3D" strokeDasharray="3 3" />
              <XAxis dataKey="date" tick={{ fill: '#94A3B8', fontSize: 10 }} />
              <YAxis tick={{ fill: '#94A3B8', fontSize: 10 }} domain={['auto', 'auto']} />
              <Tooltip contentStyle={{ backgroundColor: '#10141D', border: '1px solid #242C3D', fontSize: 12 }} />
              <Area type="monotone" dataKey="close" stroke="#3B82F6" fill="url(#priceGrad)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>

        {/* Broker Flow */}
        <ChartCard title="Institutional vs Retail Flow" testId="flow-chart">
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={chartData}>
              <CartesianGrid stroke="#242C3D" strokeDasharray="3 3" />
              <XAxis dataKey="date" tick={{ fill: '#94A3B8', fontSize: 10 }} />
              <YAxis tick={{ fill: '#94A3B8', fontSize: 10 }} />
              <Tooltip contentStyle={{ backgroundColor: '#10141D', border: '1px solid #242C3D', fontSize: 12 }} />
              <ReferenceLine y={0} stroke="#242C3D" />
              <Bar dataKey="instNet" fill="#10B981" name="Institutional" />
              <Bar dataKey="retailNet" fill="#94A3B8" name="Retail" />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        {/* Accumulation */}
        <ChartCard title="Accumulation (5d)" testId="accum-chart">
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={chartData}>
              <defs>
                <linearGradient id="accumGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#10B981" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#10B981" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="#242C3D" strokeDasharray="3 3" />
              <XAxis dataKey="date" tick={{ fill: '#94A3B8', fontSize: 10 }} />
              <YAxis tick={{ fill: '#94A3B8', fontSize: 10 }} />
              <Tooltip contentStyle={{ backgroundColor: '#10141D', border: '1px solid #242C3D', fontSize: 12 }} />
              <ReferenceLine y={0} stroke="#242C3D" />
              <Area type="monotone" dataKey="accumulation" stroke="#10B981" fill="url(#accumGrad)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>

        {/* Volume */}
        <ChartCard title="Volume" testId="volume-chart">
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={chartData}>
              <CartesianGrid stroke="#242C3D" strokeDasharray="3 3" />
              <XAxis dataKey="date" tick={{ fill: '#94A3B8', fontSize: 10 }} />
              <YAxis tick={{ fill: '#94A3B8', fontSize: 10 }} />
              <Tooltip contentStyle={{ backgroundColor: '#10141D', border: '1px solid #242C3D', fontSize: 12 }} />
              <Bar dataKey="volume" fill="#3B82F6" opacity={0.7} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Feature Breakdown */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <FeaturePanel title="My Model" score={stock.myScore} stage={stock.myStage} features={[
          { label: 'Accumulation 5d', value: stock.accumulation5d?.toLocaleString() || '0' },
          { label: 'Absorption', value: (stock.absorptionScore * 100).toFixed(0) + '%' },
          { label: 'Divergence', value: (stock.divergenceScore * 100).toFixed(0) + '%' },
          { label: 'Acceleration', value: stock.accelerationScore?.toFixed(2) },
        ]} testId="my-model-panel" />

        <FeaturePanel title="Optimal Model" score={stock.optScore} stage={stock.optStage} features={[
          { label: 'Accumulation 5d', value: stock.accumulation5d?.toLocaleString() || '0' },
          { label: 'Compression', value: (stock.compressionScore * 100).toFixed(0) + '%' },
          { label: 'Liquidity', value: (stock.liquidityScore * 100).toFixed(0) + '%' },
          { label: 'Acceleration', value: stock.accelerationScore?.toFixed(2) },
        ]} testId="opt-model-panel" />

        <FeaturePanel title="Exit Analysis" score={null} stage={null} exitMode={stock.exitMode} features={[
          { label: 'Seller Ratio', value: stock.sellerRatio?.toFixed(2), warn: stock.sellerRatio > 1.2 },
          { label: 'Inst Net', value: (stock.dailyData?.slice(-1)[0]?.instNet || 0).toLocaleString(), warn: (stock.dailyData?.slice(-1)[0]?.instNet || 0) < 0 },
          { label: 'Distribution Risk', value: (stock.distributionRiskScore * 100).toFixed(0) + '%', warn: stock.distributionRiskScore > 0.4 },
          { label: 'Entry Allowed', value: stock.entryAllowed ? 'YES' : 'NO', warn: !stock.entryAllowed },
        ]} testId="exit-panel" />
      </div>

      {/* All Features */}
      <div className="bg-trading-surface border border-trading-border rounded-sm p-4">
        <h3 className="text-xs uppercase tracking-widest text-trading-muted font-semibold mb-3">Full Feature Breakdown</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
          {[
            { label: 'Accum 3d', value: stock.accumulation3d?.toLocaleString() },
            { label: 'Accum 5d', value: stock.accumulation5d?.toLocaleString() },
            { label: 'Accum 10d', value: stock.accumulation10d?.toLocaleString() },
            { label: 'Absorption', value: (stock.absorptionScore * 100).toFixed(0) + '%' },
            { label: 'Divergence', value: (stock.divergenceScore * 100).toFixed(0) + '%' },
            { label: 'Acceleration', value: stock.accelerationScore?.toFixed(3) },
            { label: 'Compression', value: (stock.compressionScore * 100).toFixed(0) + '%' },
            { label: 'Seller Ratio', value: stock.sellerRatio?.toFixed(3) },
            { label: 'Liquidity', value: (stock.liquidityScore * 100).toFixed(0) + '%' },
            { label: 'Dist Risk', value: (stock.distributionRiskScore * 100).toFixed(0) + '%' },
            { label: 'Est. Inst Cost', value: stock.estimatedInstitutionalCost?.toFixed(2) || 'N/A' },
            { label: 'Alignment', value: stock.alignment },
          ].map(f => (
            <div key={f.label} className="bg-trading-bg/50 p-2 rounded-sm">
              <span className="text-[10px] uppercase tracking-widest text-trading-muted block">{f.label}</span>
              <span className="font-mono text-sm text-trading-text">{f.value}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ChartCard({ title, children, testId }) {
  return (
    <div data-testid={testId} className="bg-trading-surface border border-trading-border rounded-sm p-4">
      <h3 className="text-xs uppercase tracking-widest text-trading-muted font-semibold mb-3">{title}</h3>
      {children}
    </div>
  );
}

function FeaturePanel({ title, score, stage, exitMode, features, testId }) {
  return (
    <div data-testid={testId} className="bg-trading-surface border border-trading-border rounded-sm p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-xs uppercase tracking-widest text-trading-muted font-semibold">{title}</h3>
        {score !== null && (
          <div className="flex items-center gap-2">
            <span className="font-mono text-lg text-trading-text">{score?.toFixed(1)}</span>
            <StageBadge stage={stage} />
          </div>
        )}
        {exitMode && <ExitBadge exitMode={exitMode} />}
      </div>
      <div className="space-y-2">
        {features.map(f => (
          <div key={f.label} className="flex items-center justify-between">
            <span className="text-xs text-trading-muted">{f.label}</span>
            <span className={`font-mono text-sm ${f.warn ? 'text-trading-red' : 'text-trading-text'}`}>{f.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
